import { useCallback } from 'react';

import { useParams } from 'react-router';

import { isSameSymbol } from '@/entities/symbol';
import { MAX_TILES_LIMIT } from '@/features/Dashboard/constants/const';
import { useWidgetAnimation } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import { useCurrentDashboardConfig } from '@/features/Dashboard/hooks/useCurrentDashboardConfig';
import {
  getEffectiveTabName,
  getTabContent,
  normalizeTabEntry
} from '@/features/Dashboard/lib/transformers';
import { selectInitialDashboardName } from '@/features/Dashboard/pages/Edit/model/dashboardEdit.selectors';
import type { DashboardStructure, MarketApp, MarketApps } from '@/features/Dashboard/types';
import { useAppSelector } from '@/hooks/redux-hooks';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

export const useBuildDashboardStructure = () => {
  const { setElementsToAnimateId } = useWidgetAnimation();
  const params = useParams();
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const initialDashboardNameFromRedux = useAppSelector(selectInitialDashboardName);
  const initialDashboardName =
    initialDashboardNameFromRedux || (dashboardId ? decodeURIComponent(dashboardId) : '');
  const allDashboardTabs = useCurrentDashboardConfig() ?? {};
  const currentTabName = params.tabName ? decodeURIComponent(params.tabName) : undefined;
  return useCallback(
    (widget: MarketApp) => {
      const updatedTabs: Record<string, { id: string; content: MarketApps }> = {};

      const effectiveTabName = getEffectiveTabName(currentTabName);
      const hasAnyTabs = Object.keys(allDashboardTabs).length > 0;

      if (hasAnyTabs) {
        const tabNames = new Set(Object.keys(allDashboardTabs));

        // First, copy/transform all existing tabs
        Object.entries(allDashboardTabs).forEach(([tabName, tabValue]) => {
          const normalized = normalizeTabEntry(tabName, tabValue);
          if (tabName === effectiveTabName) {
            // Update current tab with new widget
            const currentTabContent = getTabContent(tabValue);
            let updatedContent: MarketApps;

            if ('type' in widget) {
              setElementsToAnimateId(widget.tiles.map(tile => tile.id));
              if (currentTabContent[0] && 'type' in currentTabContent[0]) {
                // Merge new tiles with existing tiles (avoid duplicates by symbol, respect MAX_TILES_LIMIT)
                updatedContent = currentTabContent.map((dashboardWidget, index) => {
                  if (index === 0 && 'tiles' in dashboardWidget) {
                    const existingTiles = dashboardWidget.tiles;
                    const existingSymbols = existingTiles.map(t => t.symbol.key);
                    // Filter out duplicates by symbol and merge
                    const newTiles = widget.tiles.filter(
                      t => !existingSymbols.some(es => isSameSymbol(es, t.symbol.key))
                    );
                    const mergedTiles = [...existingTiles, ...newTiles].slice(0, MAX_TILES_LIMIT);
                    return { ...dashboardWidget, tiles: mergedTiles };
                  }
                  return dashboardWidget;
                }) as MarketApps;
              } else {
                updatedContent = [widget, ...currentTabContent] as MarketApps;
              }
            } else {
              setElementsToAnimateId([widget.id]);
              updatedContent = [...currentTabContent, widget] as MarketApps;
            }

            updatedTabs[tabName] = {
              id: normalized.id,
              content: updatedContent
            };
          } else {
            // Preserve other tabs as-is
            updatedTabs[tabName] = {
              id: normalized.id,
              content: getTabContent(tabValue)
            };
          }
        });

        // If the effective tab didn't exist, create it now
        if (!tabNames.has(effectiveTabName)) {
          if ('type' in widget) {
            setElementsToAnimateId(widget.tiles.map(tile => tile.id));
          } else {
            setElementsToAnimateId([widget.id]);
          }
          updatedTabs[effectiveTabName] = {
            id: generateUniqueUUID(new Set()),
            content: [widget] as MarketApps
          };
        }
      } else {
        // Empty dashboard: create the first tab and place the widget
        if ('type' in widget) {
          setElementsToAnimateId(widget.tiles.map(tile => tile.id));
        } else {
          setElementsToAnimateId([widget.id]);
        }

        updatedTabs[effectiveTabName] = {
          id: generateUniqueUUID(new Set()),
          content: [widget] as MarketApps
        };
      }

      const structurePayload: DashboardStructure = {
        [initialDashboardName]: updatedTabs
      };
      return structurePayload;
    },
    [allDashboardTabs, currentTabName, initialDashboardName, setElementsToAnimateId]
  );
};
